// Organization category mapping between display names and database values

export interface OrganizationCategoryMapping {
  displayName: string;
  dbValue: string;
  description: string;
}

// Database organization category values from backend
export const DB_ORGANIZATION_CATEGORIES = [
  "for_profit_business",
  "nonprofit_ngo",
  "government_public_sector",
  "educational_research_institution",
  "international_intergovernmental",
  "other",
] as const;

export type DbOrganizationCategory = (typeof DB_ORGANIZATION_CATEGORIES)[number];

// Comprehensive mapping between display names and database values
export const ORGANIZATION_CATEGORY_MAPPINGS: OrganizationCategoryMapping[] = [
  {
    displayName: "For-Profit Business",
    dbValue: "for_profit_business",
    description: "Commercial enterprises operating for profit",
  },
  {
    displayName: "Nonprofit / NGO",
    dbValue: "nonprofit_ngo",
    description: "Non-governmental organizations and charities",
  },
  {
    displayName: "Government / Public Sector",
    dbValue: "government_public_sector",
    description: "Government agencies and public sector organizations",
  },
  {
    displayName: "Educational / Research Institution",
    dbValue: "educational_research_institution",
    description: "Universities, schools, and research organizations",
  },
  {
    displayName: "International / Intergovernmental",
    dbValue: "international_intergovernmental",
    description: "International organizations and intergovernmental bodies",
  },
  {
    displayName: "Other",
    dbValue: "other",
    description: "Other organization types",
  },
];

/**
 * Get display name from database value
 * @param dbValue - The snake_case database value
 * @returns Human-readable display name
 */
export const getDisplayNameFromDbValue = (dbValue: string): string => {
  const mapping = ORGANIZATION_CATEGORY_MAPPINGS.find(
    (m) => m.dbValue === dbValue
  );
  return mapping?.displayName || dbValue;
};

/**
 * Get database value from display name
 * @param displayName - The human-readable display name
 * @returns Database snake_case value
 */
export const getDbValueFromDisplayName = (displayName: string): string => {
  const mapping = ORGANIZATION_CATEGORY_MAPPINGS.find(
    (m) => m.displayName === displayName
  );
  return mapping?.dbValue || displayName.toLowerCase().replace(/\s+/g, "_");
};

/**
 * Convert array of database values to display names
 * @param dbValues - Array of snake_case database values
 * @returns Array of human-readable display names
 */
export const getDbValuesFromDisplayNames = (
  displayNames: string[]
): string[] => {
  return displayNames.map(getDbValueFromDisplayName);
};

/**
 * Convert array of display names to database values
 * @param displayNames - Array of human-readable display names
 * @returns Array of snake_case database values
 */
export const getDisplayNamesFromDbValues = (dbValues: string[]): string[] => {
  // Filter to only return display names for values that exist in our mapping
  return dbValues
    .map(getDisplayNameFromDbValue)
    .filter((displayName, index) => {
      // Only include if the mapping exists (not just the fallback dbValue)
      const originalDbValue = dbValues[index];
      const hasMapping = ORGANIZATION_CATEGORY_MAPPINGS.some(
        (m) => m.dbValue === originalDbValue
      );
      return hasMapping;
    });
};

/**
 * Get all display names for dropdown/selection
 * @returns Sorted array of all display names
 */
export const getAllCategoryDisplayNames = (): string[] => {
  return ORGANIZATION_CATEGORY_MAPPINGS.map((m) => m.displayName);
};

/**
 * Get all database values
 * @returns Array of all database values
 */
export const getAllCategoryDbValues = (): string[] => {
  return ORGANIZATION_CATEGORY_MAPPINGS.map((m) => m.dbValue);
};
